import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus, DollarSign, TrendingUp, Package,
  Edit2, Trash2, Download, Calendar,
  PieChart, BarChart3, Users, Phone, Mail, MapPin, ShoppingBag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SalesCharts from './sales/SalesCharts';
import SaleFormDialog from './sales/SaleFormDialog';
import CustomerFormDialog from './sales/CustomerFormDialog';
import CustomerDetailDialog from './sales/CustomerDetailDialog';
import CollapsibleSection from './ui/CollapsibleSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product type configuration
const PRODUCT_TYPES = {
  honey: { label: 'Honey', icon: '🍯', color: '#f59e0b', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  nuc: { label: 'Nuc', icon: '📦', color: '#8b5cf6', bgColor: 'bg-violet-50', textColor: 'text-violet-700' },
  queen: { label: 'Queen', icon: '👑', color: '#ec4899', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  bee_package: { label: 'Bee Package', icon: '🐝', color: '#14b8a6', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  hive: { label: 'Hive', icon: '🏠', color: '#f97316', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  equipment: { label: 'Equipment', icon: '🔧', color: '#6b7280', bgColor: 'bg-stone-50', textColor: 'text-stone-700' },
};

const CHART_COLORS = ['#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6b7280', '#3b82f6', '#10b981'];

const SalesView = ({ token, yards = [] }) => {
  const [activeTab, setActiveTab] = useState('sales');
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  
  // Honey-specific state
  const [harvestConfig, setHarvestConfig] = useState(null);
  const [honeyInventory, setHoneyInventory] = useState([]);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    product_type: 'honey',
    product_name: '',
    quantity: 1,
    unit_price: 0,
    customer_name: '',
    customer_contact: '',
    yard: '',
    notes: '',
    payment_method: 'cash',
    payment_status: 'paid',
    // Honey-specific fields
    honey_type: '',
    container_type: '',
    quantity_kg: 0
  });

  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchSales();
    fetchSummary();
    fetchCustomers();
    fetchHarvestConfig();
    fetchHoneyInventory();
  }, []);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/sales`, { headers });
      setSales(res.data || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/sales/summary`, { headers });
      setSummary(res.data);
    } catch (err) {
    }
  };

  const fetchCustomers = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/customers`, { headers });
      setCustomers(res.data || []);
    } catch (err) {
    }
  };

  // Fetch harvest config for honey types and container types
  const fetchHarvestConfig = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/harvest-config`, { headers });
      setHarvestConfig(res.data);
    } catch (err) {
    }
  };

  // Fetch honey inventory (harvests minus sold)
  const fetchHoneyInventory = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/honey-inventory`, { headers });
      setHoneyInventory(res.data || []);
    } catch (err) {
      // Fallback: try to calculate from harvests
      try {
        const fallbackHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const harvestRes = await axios.get(`${API}/harvests`, { headers: fallbackHeaders });
        const harvests = harvestRes.data || [];
        // Group by honey type and calculate totals
        const inventory = {};
        harvests.forEach(h => {
          const type = h.honey_type || 'Unspecified';
          if (!inventory[type]) {
            inventory[type] = { honey_type: type, total_kg: 0, harvest_count: 0 };
          }
          inventory[type].total_kg += parseFloat(h.quantity) || 0;
          inventory[type].harvest_count += 1;
        });
        setHoneyInventory(Object.values(inventory));
      } catch (fallbackErr) {
      }
    }
  };

  // Handle honey type selection - auto-populate price
  const handleHoneyTypeChange = (honeyType) => {
    // Normalize honey types to handle string format
    const normalizedTypes = (harvestConfig?.honey_types || [])
      .map(t => typeof t === 'string' ? { id: t, name: t, is_active: true, price_per_unit: 0 } : t);
    const selectedType = normalizedTypes.find(t => t.name === honeyType);
    const price = selectedType?.price_per_unit || 0;
    setFormData({ 
      ...formData, 
      honey_type: honeyType,
      product_name: honeyType ? `${honeyType} Honey` : '',
      unit_price: price
    });
  };

  // Handle container type selection - calculate quantity_kg
  const handleContainerChange = (containerType) => {
    const selectedContainer = harvestConfig?.container_types?.find(c => c.name === containerType || c.id === containerType);
    const capacity = selectedContainer?.capacity_kg || 1;
    setFormData({
      ...formData,
      container_type: containerType,
      quantity_kg: formData.quantity * capacity
    });
  };

  // Update quantity_kg when quantity changes
  const handleQuantityChange = (qty) => {
    const selectedContainer = harvestConfig?.container_types?.find(c => c.name === formData.container_type || c.id === formData.container_type);
    const capacity = selectedContainer?.capacity_kg || 1;
    setFormData({
      ...formData,
      quantity: qty,
      quantity_kg: qty * capacity
    });
  };

  const handleSubmit = async () => {
    if (!formData.product_type) {
      toast.error('Please select a product type');
      return;
    }
    
    // Validate honey-specific requirements
    if (formData.product_type === 'honey' && !formData.honey_type) {
      toast.error('Please select a honey type');
      return;
    }
    
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Calculate total price based on product type
      let totalPrice;
      if (formData.product_type === 'honey') {
        // For honey: total = quantity_kg * unit_price (price per kg)
        totalPrice = (formData.quantity_kg || 0) * formData.unit_price;
      } else {
        // For other products: total = quantity * unit_price
        totalPrice = formData.quantity * formData.unit_price;
      }
      
      const payload = {
        ...formData,
        total_price: totalPrice
      };
      
      if (editingSale) {
        await axios.put(`${API}/sales/${editingSale.id}`, payload, { headers });
        toast.success('Sale updated');
      } else {
        await axios.post(`${API}/sales`, payload, { headers });
        toast.success('Sale recorded');
      }
      
      setShowAddDialog(false);
      setEditingSale(null);
      resetForm();
      fetchSales();
      fetchSummary();
      fetchHoneyInventory(); // Refresh inventory after sale
    } catch (err) {
      toast.error('Failed to save sale');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale?')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/sales/${saleId}`, { headers });
      toast.success('Sale deleted');
      fetchSales();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete sale');
    }
  };

  const openEditDialog = (sale) => {
    setEditingSale(sale);
    setFormData({
      date: sale.date || '',
      product_type: sale.product_type || 'honey',
      product_name: sale.product_name || '',
      quantity: sale.quantity || 1,
      unit_price: sale.unit_price || 0,
      customer_name: sale.customer_name || '',
      customer_contact: sale.customer_contact || '',
      yard: sale.yard || '',
      notes: sale.notes || '',
      payment_method: sale.payment_method || 'cash',
      payment_status: sale.payment_status || 'paid',
      // Honey-specific fields
      honey_type: sale.honey_type || '',
      container_type: sale.container_type || '',
      quantity_kg: sale.quantity_kg || 0
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      product_type: 'honey',
      product_name: '',
      quantity: 1,
      unit_price: 0,
      customer_name: '',
      customer_contact: '',
      yard: '',
      notes: '',
      payment_method: 'cash',
      payment_status: 'paid',
      // Honey-specific fields
      honey_type: '',
      container_type: '',
      quantity_kg: 0
    });
  };

  // Customer functions
  const handleSaveCustomer = async () => {
    if (!customerFormData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, customerFormData, { headers });
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, customerFormData, { headers });
        toast.success('Customer added');
      }
      setShowCustomerDialog(false);
      setEditingCustomer(null);
      resetCustomerForm();
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/customers/${customerId}`, { headers });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const openEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
    setShowCustomerDialog(true);
  };

  const viewCustomerHistory = async (customer) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/customers/${customer.id}`, { headers });
      setSelectedCustomer(res.data);
    } catch (err) {
      toast.error('Failed to load customer details');
    }
  };

  const resetCustomerForm = () => {
    setCustomerFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      notes: ''
    });
  };

  // Calculate customer stats
  const customerStats = useMemo(() => {
    const stats = {};
    sales.forEach(sale => {
      const name = sale.customer_name || 'Walk-in';
      if (!stats[name]) {
        stats[name] = { count: 0, total: 0 };
      }
      stats[name].count += 1;
      stats[name].total += sale.total_price || 0;
    });
    return stats;
  }, [sales]);

  // Prepare chart data
  const pieChartData = useMemo(() => {
    if (!summary?.by_product_type) return [];
    return Object.entries(summary.by_product_type)
      .filter(([_, data]) => data.revenue > 0)
      .map(([type, data]) => ({
        name: PRODUCT_TYPES[type]?.label || type,
        value: data.revenue,
        color: PRODUCT_TYPES[type]?.color || '#6b7280'
      }));
  }, [summary]);

  const monthlyChartData = useMemo(() => {
    if (!summary?.by_month) return [];
    return Object.entries(summary.by_month)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, data]) => ({
        month: month.slice(5), // MM format
        revenue: data.revenue,
        count: data.count
      }));
  }, [summary]);

  const yardChartData = useMemo(() => {
    if (!summary?.by_yard) return [];
    return Object.entries(summary.by_yard)
      .filter(([yard, data]) => data.revenue > 0 && yard && yard.trim() !== "")
      .map(([yard, data]) => ({
        name: yard || "Unspecified",
        revenue: data.revenue,
        count: data.count
      }));
  }, [summary]);

  // Filter sales
  const filteredSales = useMemo(() => {
    let filtered = sales;
    if (filterType !== 'all') {
      filtered = filtered.filter(s => s.product_type === filterType);
    }
    if (filterYear !== 'all') {
      filtered = filtered.filter(s => s.date?.startsWith(filterYear));
    }
    return filtered;
  }, [sales, filterType, filterYear]);

  const years = useMemo(() => {
    const yearSet = new Set(sales.map(s => s.date?.slice(0, 4)).filter(Boolean));
    return [...yearSet].sort().reverse();
  }, [sales]);

  // Export to CSV
  const exportCSV = () => {
    let csv = '';
    csv += '"SALES REPORT"\n';
    csv += `"Generated: ${new Date().toLocaleDateString()}"\n\n`;
    
    // Summary
    csv += '"SUMMARY"\n';
    csv += `"Total Revenue","$${(summary?.total_revenue || 0).toFixed(2)}"\n\n`;
    
    // By Product Type
    csv += '"REVENUE BY PRODUCT TYPE"\n';
    csv += '"Product","Count","Revenue"\n';
    Object.entries(summary?.by_product_type || {}).forEach(([type, data]) => {
      csv += `"${PRODUCT_TYPES[type]?.label || type}","${data.count}","$${data.revenue.toFixed(2)}"\n`;
    });
    csv += '\n';
    
    // By Yard
    csv += '"REVENUE BY APIARY"\n';
    csv += '"Apiary","Count","Revenue"\n';
    Object.entries(summary?.by_yard || {}).forEach(([yard, data]) => {
      csv += `"${yard}","${data.count}","$${data.revenue.toFixed(2)}"\n`;
    });
    csv += '\n';
    
    // All Sales
    csv += '"ALL SALES"\n';
    csv += '"Date","Product Type","Product Name","Qty","Unit Price","Total","Customer","Apiary","Payment"\n';
    filteredSales.forEach(sale => {
      csv += `"${sale.date}","${PRODUCT_TYPES[sale.product_type]?.label || sale.product_type}","${sale.product_name}","${sale.quantity}","$${sale.unit_price}","$${sale.total_price || (sale.quantity * sale.unit_price)}","${sale.customer_name}","${sale.yard}","${sale.payment_status}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Sales report exported');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-amber-600" /> Sales & Customers
        </h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} className="hidden sm:flex">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {activeTab === 'sales' ? (
            <Button onClick={() => { resetForm(); setEditingSale(null); setShowAddDialog(true); }} className="bg-green-600 hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />
              Record Sale
            </Button>
          ) : (
            <Button onClick={() => { resetCustomerForm(); setEditingCustomer(null); setShowCustomerDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Customer
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-green-700">${(summary?.total_revenue || 0).toFixed(0)}</div>
            <div className="text-xs text-green-600">Total Revenue</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">
              ${(summary?.by_product_type?.honey?.revenue || 0).toFixed(0)}
            </div>
            <div className="text-xs text-amber-500">Honey Sales</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-violet-600">
              ${((summary?.by_product_type?.nuc?.revenue || 0) + (summary?.by_product_type?.queen?.revenue || 0) + (summary?.by_product_type?.bee_package?.revenue || 0)).toFixed(0)}
            </div>
            <div className="text-xs text-violet-500">Bee Sales</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-stone-600">
              {sales.length + (summary?.by_product_type?.honey?.count || 0)}
            </div>
            <div className="text-xs text-stone-500">Total Sales</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {customers.length}
            </div>
            <div className="text-xs text-blue-500">Customers</div>
          </div>
        </div>
      </div>

      {/* Tabs for Sales and Customers */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="sales" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Sales
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Customers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="space-y-3 mt-4">
          <SalesCharts summary={summary} pieChartData={pieChartData} monthlyChartData={monthlyChartData} yardChartData={yardChartData} />

      {/* Sales History */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h3 className="font-semibold text-stone-800 flex items-center gap-2">
            📋 Sales History
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              {filteredSales.length} records
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-32 h-8">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(PRODUCT_TYPES).map(([type, config]) => (
                  <SelectItem key={type} value={type}>{config.icon} {config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-28 h-8">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="divide-y divide-stone-100 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-stone-400">Loading sales...</div>
          ) : filteredSales.length === 0 ? (
            <div className="p-8 text-center text-stone-400">
              No sales recorded yet. Click &ldquo;Record Sale&rdquo; to add your first sale.
            </div>
          ) : (
            filteredSales.map(sale => {
              const productConfig = PRODUCT_TYPES[sale.product_type] || PRODUCT_TYPES.equipment;
              const total = sale.total_price || (sale.quantity * sale.unit_price);
              return (
                <div key={sale.id} className="p-4 hover:bg-stone-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`${productConfig.bgColor} ${productConfig.textColor} px-2 py-0.5 rounded text-sm font-medium flex items-center gap-1`}>
                          {productConfig.icon} {productConfig.label}
                        </span>
                        <span className="text-stone-500 text-sm">{formatDate(sale.date)}</span>
                        {sale.yard && (
                          <span className="text-stone-400 text-xs">📍 {sale.yard}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold text-stone-800">
                          {sale.product_name || productConfig.label}
                        </span>
                        <span className="text-stone-500">
                          {sale.quantity} × ${sale.unit_price} = <span className="font-bold text-green-600">${total.toFixed(2)}</span>
                        </span>
                      </div>
                      {sale.customer_name && (
                        <div className="text-sm text-stone-500">
                          Customer: {sale.customer_name}
                        </div>
                      )}
                      {sale.notes && (
                        <p className="text-sm text-stone-400 italic">&ldquo;{sale.notes}&rdquo;</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${sale.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {sale.payment_status}
                      </span>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditDialog(sale)} data-testid={`edit-sale-${sale.id}`}>
                        <Edit2 className="h-4 w-4 text-stone-400" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(sale.id)} data-testid={`delete-sale-${sale.id}`}>
                        <Trash2 className="h-4 w-4 text-stone-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4 mt-4">
          {/* Customer List */}
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
            <div className="p-4 border-b border-stone-100">
              <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                👥 Customer Directory
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {customers.length} customers
                </span>
              </h3>
            </div>

            <div className="divide-y divide-stone-100 max-h-[500px] overflow-y-auto">
              {customers.length === 0 ? (
                <div className="p-8 text-center text-stone-400">
                  No customers yet. Click &ldquo;Add Customer&rdquo; to add your first customer.
                </div>
              ) : (
                customers.map(customer => {
                  const stats = customerStats[customer.name] || { count: 0, total: 0 };
                  return (
                    <div 
                      key={customer.id} 
                      className="p-4 hover:bg-stone-50 transition-colors cursor-pointer"
                      onClick={() => viewCustomerHistory(customer)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                              {customer.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <span className="font-medium text-stone-800">{customer.name}</span>
                              <div className="flex items-center gap-3 text-sm text-stone-500 mt-0.5">
                                {customer.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {customer.email}
                                  </span>
                                )}
                                {customer.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {customer.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {customer.address && (
                            <p className="text-sm text-stone-400 mt-1 flex items-center gap-1 ml-13">
                              <MapPin className="h-3 w-3" />
                              {customer.address}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium text-green-600">${stats.total.toFixed(0)}</div>
                          <div className="text-xs text-stone-400">{stats.count} purchases</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={(e) => { e.stopPropagation(); openEditCustomer(customer); }}
                          >
                            <Edit2 className="h-4 w-4 text-stone-400" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer.id); }}
                          >
                            <Trash2 className="h-4 w-4 text-stone-400" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Top Customers Chart */}
          {Object.keys(customerStats).length > 0 && (
            <CollapsibleSection
              title="Top Customers"
              icon={Users}
              defaultOpen={true}
              badge={`${Object.keys(customerStats).length} customers`}
              badgeColor="stone"
            >
              <div className="pt-4">
                <div className="space-y-2">
                  {Object.entries(customerStats)
                    .sort(([,a], [,b]) => b.total - a.total)
                    .slice(0, 10)
                    .map(([name, stats], index) => (
                      <div key={name} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-stone-700">{name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-green-600">${stats.total.toFixed(0)}</span>
                          <span className="text-xs text-stone-400 ml-2">({stats.count} orders)</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </CollapsibleSection>
          )}
        </TabsContent>
      </Tabs>

      {/* Customer Detail Modal */}
      <CustomerDetailDialog customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} formatDate={formatDate} />

      {/* Add/Edit Customer Dialog */}
      <CustomerFormDialog
        open={showCustomerDialog}
        onOpenChange={setShowCustomerDialog}
        editingCustomer={editingCustomer}
        customerFormData={customerFormData}
        setCustomerFormData={setCustomerFormData}
        handleSaveCustomer={handleSaveCustomer}
        saving={saving}
      />

      {/* Add/Edit Sale Dialog */}
      <SaleFormDialog
        open={showAddDialog}
        onOpenChange={(v) => { setShowAddDialog(v); if (!v) setEditingSale(null); }}
        editingSale={editingSale}
        formData={formData}
        setFormData={setFormData}
        honeyInventory={honeyInventory}
        harvestConfig={harvestConfig}
        handleHoneyTypeChange={handleHoneyTypeChange}
        handleContainerChange={handleContainerChange}
        handleQuantityChange={handleQuantityChange}
        yards={yards}
        handleSubmit={handleSubmit}
        saving={saving}
      />
    </div>
  );
};

export default SalesView;
